/**
 * Clients page — list all tenants + create new client.
 * GET /api/clients, POST /api/clients.
 * Client selector in header updates active client.
 */

import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Building2, Plus, MessageCircle, TrendingUp } from 'lucide-react'
import { useClientsList, useCreateClient, useActiveClient } from '@/hooks/useClients'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ClientListItem } from '@/types'

const STATUS_LABELS: Record<string, string> = {
  active: 'Активен',
  prospect: 'Лид',
  paused: 'На паузе',
  churned: 'Ушёл',
}

const STATUS_VARIANTS: Record<string, 'gate_passed' | 'urgency_yellow' | 'secondary' | 'destructive'> = {
  active: 'gate_passed',
  prospect: 'urgency_yellow',
  paused: 'secondary',
  churned: 'destructive',
}

const STAGE_LABELS: Record<string, string> = {
  sales: 'Продажа',
  delivery: 'Внедрение',
  account: 'Сопровождение',
  terminal: 'Завершён',
}

// ---- Create client dialog (inline) ----

function CreateClientForm({ onClose }: { onClose: () => void }) {
  const createClient = useCreateClient()
  const [name, setName] = useState('')
  const [channel, setChannel] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) { setError('Введите название клиента'); return }
    setError(null)
    try {
      await createClient.mutateAsync({ name: name.trim(), channel: channel.trim() || undefined })
      onClose()
    } catch {
      setError('Ошибка при создании клиента')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Новый клиент</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Название компании / клиента *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ООО «Ромашка»"
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Канал (опционально)</label>
              <input
                type="text"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                placeholder="telegram / avito / max"
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Отмена
              </Button>
              <Button type="submit" size="sm" disabled={createClient.isPending}>
                {createClient.isPending ? 'Создаём...' : 'Создать'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

// ---- Client row ----

function ClientRow({ client }: { client: ClientListItem }) {
  const { activeClientId, setActiveClientId } = useActiveClient()
  const isActive = activeClientId === client.id

  // Карточка сделки живёт на /contact/$id, где id — это dealId (см. ContactPage).
  // Нет активной сделки — вести некуда, строка остаётся некликабельной.
  const dealId = client.deal_id

  const body = (
    <>
      <Building2 className="h-8 w-8 text-muted-foreground shrink-0" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium">{client.name}</p>
          <Badge variant={STATUS_VARIANTS[client.status] ?? 'secondary'}>
            {STATUS_LABELS[client.status] ?? client.status}
          </Badge>
          {client.stage && (
            <Badge variant="outline" className="text-xs">
              {STAGE_LABELS[client.stage] ?? client.stage}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            {client.counts?.open_deals ?? 0} сделок
          </span>
          <span className="flex items-center gap-1">
            <MessageCircle className="h-3 w-3" />
            {client.counts?.dialogs_today ?? 0} диалогов сегодня
          </span>
          {client.channel && <span>Канал: {client.channel}</span>}
        </div>
      </div>
    </>
  )

  return (
    <div
      className={`flex items-center gap-4 rounded-lg border p-4 transition-colors ${isActive ? 'bg-accent border-primary' : 'bg-card hover:bg-accent/30'} ${dealId ? 'cursor-pointer' : ''}`}
    >
      {dealId ? (
        <Link
          to="/contact/$id"
          params={{ id: dealId }}
          className="flex flex-1 min-w-0 items-center gap-4"
        >
          {body}
        </Link>
      ) : (
        body
      )}

      <Button
        variant={isActive ? 'default' : 'outline'}
        size="sm"
        onClick={() => setActiveClientId(isActive ? null : client.id)}
        className="shrink-0"
      >
        {isActive ? 'Выбран' : 'Выбрать'}
      </Button>
    </div>
  )
}

// ---- Main page ----

export function ClientsPage() {
  const { data: clients = [], isLoading, error } = useClientsList()
  const [showCreate, setShowCreate] = useState(false)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Клиенты</h1>
          <p className="text-sm text-muted-foreground">
            {clients.length} клиентов · активный клиент применяется во всём кабинете
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" />
          Новый клиент
        </Button>
      </div>

      {/* List */}
      {isLoading && <p className="text-sm text-muted-foreground">Загрузка...</p>}
      {error && <p className="text-sm text-destructive">Ошибка загрузки клиентов</p>}

      {!isLoading && clients.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
          <Building2 className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Клиентов пока нет</p>
          <Button size="sm" className="mt-4" onClick={() => setShowCreate(true)}>
            + Добавить клиента
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {clients.map((client) => (
          <ClientRow key={client.id} client={client} />
        ))}
      </div>

      {showCreate && <CreateClientForm onClose={() => setShowCreate(false)} />}
    </div>
  )
}
